import { type FC, type ReactNode, useEffect, useState } from 'react';
import { ButtonProps } from 'grommet/components';
import { AddButton, Box, BoxProps } from '/src/components';

const radius = '6px';

export type TToggleButtonGroupOption = {
  icon?: ReactNode;
  label?: string;
  value: string | number;
  tooltip?: string;
};

export const ToggleButtonGroup: FC<ToggleButtonGroupProps> = (props) => {
  const { options, disabled, value, onChange, ...boxProps } = props;

  const [isHover, setIsHover] = useState(false);
  const [hoverOption, setHoverOption] = useState<string | number | null>(null);
  const [selected, setSelected] = useState<string | number | null>(options?.[0]?.value);

  useEffect(() => {
    if (options) {
      setSelected(value !== undefined ? value : options[0]?.value);
    }
  }, [options, value]);

  const handleClick = (value: string | number) => {
    setSelected(value);
    onChange?.(value);
  };

  return (
    <Box
      direction="row"
      overflow="hidden"
      pad="0"
      elevation={isHover ? 'small' : undefined}
      border={{ color: 'light-2', size: '0.05rem' }}
      borderRadius={radius}
      {...boxProps}
    >
      {options.map((opt, idx) => {
        const isSelected = selected === opt.value;
        const isFirst = idx === 0;
        const isLast = idx === options.length - 1;

        const radiusStyle = {
          borderTopLeftRadius: isFirst ? radius : '0px',
          borderBottomLeftRadius: isFirst ? radius : '0px',
          borderTopRightRadius: isLast ? radius : '0px',
          borderBottomRightRadius: isLast ? radius : '0px',
        };

        const bgStyle = isSelected
          ? hoverOption === opt.value
            ? 'button-hover'
            : 'accent-1'
          : hoverOption === opt.value
            ? 'light-5'
            : 'light-4';

        return (
          <AddButton
            key={opt.value}
            label={opt.label}
            icon={opt.icon}
            active={isSelected}
            primary={isSelected}
            disabled={disabled}
            background={bgStyle}
            color={isSelected ? 'white' : 'inherit'}
            style={{ ...radiusStyle }}
            tip={opt.tooltip}
            onMouseOver={() => {
              setIsHover(true);
              setHoverOption(opt.value);
            }}
            onMouseOut={() => {
              setIsHover(false);
              setHoverOption(null);
            }}
            onClick={() => handleClick(opt.value)}
          />
        );
      })}
    </Box>
  );
};

export type ToggleButtonGroupProps = BoxProps & {
  options: TToggleButtonGroupOption[];
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
  disabled?: ButtonProps['disabled'];
};
